/**
 * Admin Frequent-Visitor profile (360° view).
 *
 * Reached from /frequent-visitors (row name → Link). Mirrors the resident-detail
 * layout: header card with a large passport-style photo (140×170, rounded-vt-md)
 * + name + status badges, followed by stacked <Card>s for Personal, KYC, ID
 * card, Assigned flats, Attendance history, and a recent Audit trail.
 *
 * Server endpoints reused:
 *   GET /v1/frequent-visitors/:id                       — single FV row (added 2026-06-12)
 *   GET /v1/frequent-visitors/:id/photo                 — auth-blob stream (existing)
 *   GET /v1/frequent-visitors/:id/assignments           — assignments list (existing)
 *   GET /v1/frequent-visitors/:id/attendance            — attendance (existing)
 *   GET /v1/fv-kinds                                    — catalog labels (existing)
 *   GET /v1/audit-log?entity_type=frequent_visitor&...  — audit trail (existing)
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  CalendarClock,
  CalendarDays,
  Clock3,
  Download,
  FileText,
  History,
  Home,
  Image as ImageIcon,
  Link2,
  Phone,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  UserCircle2
} from 'lucide-react';
import { api, get, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatDate, formatDateTime } from '@/lib/format';

// ───────────── types (shared shape with /frequent-visitors list) ─────────────

type Status = 'pending' | 'verified' | 'rejected' | 'expired';
type PoliceStatus = 'not_started' | 'pending' | 'verified' | 'rejected';
type IdProofType = 'aadhaar' | 'pan' | 'driving_licence' | 'passport' | 'other';
type ApprovalStatus = 'auto_approved' | 'pending_resident' | 'approved' | 'rejected';
type FvAttendanceKind = 'in' | 'out';

interface FrequentVisitor {
  id: string;
  society_id: string;
  full_name: string;
  phone: string;
  kind: string;
  status: Status;
  photo_url: string | null;
  photo_storage_key: string | null;
  id_proof_type: IdProofType | null;
  id_proof_last4: string | null;
  id_proof_url: string | null;
  id_proof_storage_key: string | null;
  police_verification_status: PoliceStatus;
  police_verification_url: string | null;
  police_verification_storage_key: string | null;
  police_verified_at: string | null;
  id_card_qr_token: string | null;
  id_card_issued_at: string | null;
  alt_phone: string | null;
  dob: string | null;
  permanent_address: string | null;
  current_address: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  notes: string | null;
  rejection_reason: string | null;
  verified_at: string | null;
  active: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Assignment {
  id: string;
  frequent_visitor_id: string;
  unit_id: string;
  added_by_resident_id: string | null;
  days_of_week: string | null;
  time_start: string | null;
  time_end: string | null;
  approval_status: ApprovalStatus;
  approval_decided_at: string | null;
  approval_rejection_reason: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
  unit: { id: string; number: string; block: { id: string; name: string } | null };
  added_by: { id: string; full_name: string; phone: string } | null;
}

interface AssignmentListResp {
  items: Assignment[];
}

interface AttendanceRow {
  id: string;
  frequent_visitor_id: string;
  unit_id: string | null;
  unit: { number: string; block: { name: string } | null } | null;
  kind: FvAttendanceKind;
  ts: string;
  date_local: string;
  guard_id: string | null;
  gate_id: string | null;
  scan_method: 'manual' | 'qr_scan' | 'face_match';
  notes: string | null;
}

interface AttendanceListResp {
  items: AttendanceRow[];
  next_cursor: string | null;
}

type VerificationLevel = 'recommended' | 'light';
interface KindRow {
  id: string;
  code: string;
  label: string;
  verification_level: VerificationLevel;
  issue_id_card: boolean;
  manual_capture_photo: boolean;
  active: boolean;
}

interface AuditLogRow {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  actor_user_id: string | null;
  actor_role: string;
  at: string;
  before: unknown;
  after: unknown;
}

interface AuditLogListResp {
  items: AuditLogRow[];
  next_cursor: string | null;
}

// ───────────── badge helpers ─────────────

const STATUS_VARIANT: Record<Status, 'warn' | 'ok' | 'alert' | 'muted'> = {
  pending: 'warn',
  verified: 'ok',
  rejected: 'alert',
  expired: 'muted'
};

const STATUS_LABEL: Record<Status, string> = {
  pending: 'Pending review',
  verified: 'Verified',
  rejected: 'Rejected',
  expired: 'Expired'
};

const POLICE_VARIANT: Record<PoliceStatus, 'warn' | 'ok' | 'alert' | 'muted'> = {
  not_started: 'muted',
  pending: 'warn',
  verified: 'ok',
  rejected: 'alert'
};

const POLICE_LABEL: Record<PoliceStatus, string> = {
  not_started: 'Not started',
  pending: 'Under review',
  verified: 'Police verified',
  rejected: 'Police rejected'
};

const ID_PROOF_LABEL: Record<IdProofType, string> = {
  aadhaar: 'Aadhaar',
  pan: 'PAN',
  driving_licence: 'Driving licence',
  passport: 'Passport',
  other: 'Other'
};

const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  auto_approved: 'Auto-approved',
  approved: 'Approved',
  pending_resident: 'Pending resident',
  rejected: 'Rejected'
};

const APPROVAL_STATUS_VARIANT: Record<ApprovalStatus, 'ok' | 'warn' | 'alert' | 'muted'> = {
  auto_approved: 'ok',
  approved: 'ok',
  pending_resident: 'warn',
  rejected: 'alert'
};

const DAY_LABEL: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun'
};

/**
 * Friendly action label for FV-scoped audit events. Falls back to raw code.
 */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  'frequent_visitor.add': 'Added by admin',
  'frequent_visitor.nominate': 'Nominated',
  'frequent_visitor.verify': 'Verified',
  'frequent_visitor.reject': 'Rejected',
  'frequent_visitor.update': 'Profile updated',
  'frequent_visitor.delete': 'Removed',
  'frequent_visitor.id_card_issue': 'ID card issued',
  'frequent_visitor.id_card_regenerate': 'ID card re-issued'
};

// ───────────── helpers ─────────────

function initialsFor(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Auth-blob helper — same pattern as the FV list page. Streams a private
 * /v1/frequent-visitors/:id/photo (or /id-proof) blob via the api client and
 * returns a temporary object URL.
 */
function useFvBlobUrl(
  fvId: string | null | undefined,
  kind: 'photo' | 'id-proof' | 'police-verification',
  cacheBust: string | null | undefined
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fvId || !cacheBust) {
      setUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>(`/frequent-visitors/${fvId}/${kind}`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [fvId, kind, cacheBust]);
  return url;
}

// ───────────── section + label-value primitives ─────────────

function SectionTitle({
  icon,
  title,
  subtitle,
  action
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-vt-3 flex items-start justify-between gap-vt-3">
      <div className="flex items-start gap-vt-2">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
          {icon}
        </span>
        <div>
          <h2 className="text-vt-16 font-semibold tracking-[-0.01em] text-vt-text">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-vt-12 text-vt-text-muted">{subtitle}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function LabelValue({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-vt-12 uppercase tracking-wide text-vt-text-soft">{label}</span>
      <span className="text-vt-13 text-vt-text">{children}</span>
    </div>
  );
}

// ───────────── page ─────────────

export function FrequentVisitorDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();

  // 1. FV core
  const fvQ = useQuery({
    queryKey: ['fv-detail', id],
    queryFn: () => get<FrequentVisitor>(`/frequent-visitors/${id}`),
    enabled: Boolean(id)
  });
  const fv = fvQ.data;

  // 2. Catalog labels — translate FV.kind code → human label + verification policy.
  const kindsQ = useQuery({
    queryKey: ['fv-kinds'],
    queryFn: () => get<{ items: KindRow[] }>('/fv-kinds')
  });
  const kindRow = useMemo(
    () => (fv ? kindsQ.data?.items.find((k) => k.code === fv.kind) ?? null : null),
    [fv, kindsQ.data]
  );

  // 3. Assignments (flats this FV is linked to).
  const assignmentsQ = useQuery({
    queryKey: ['fv-assignments', id],
    queryFn: () => get<AssignmentListResp>(`/frequent-visitors/${id}/assignments`),
    enabled: Boolean(id)
  });

  // 4. Attendance — last 14 days.
  const dateFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  }, []);
  const attendanceQ = useQuery({
    queryKey: ['fv-attendance', id, dateFrom],
    queryFn: () =>
      get<AttendanceListResp>(`/frequent-visitors/${id}/attendance`, {
        params: { date_from: dateFrom, limit: 50 }
      }),
    enabled: Boolean(id)
  });

  // 5. Audit log scoped to entity_type=frequent_visitor + entity_id.
  const auditQ = useQuery({
    queryKey: ['fv-audit', id],
    queryFn: () =>
      get<AuditLogListResp>('/audit-log', {
        params: { entity_type: 'frequent_visitor', entity_id: id, limit: 10 }
      }),
    enabled: Boolean(id)
  });

  // 6. Photo + ID-proof previews via auth-blob.
  const photoUrl = useFvBlobUrl(id, 'photo', fv?.photo_storage_key ?? null);
  const idProofUrl = useFvBlobUrl(id, 'id-proof', fv?.id_proof_storage_key ?? null);

  // ID-card SVG download (auth-blob → blob URL → anchor). Same approach as
  // the list page — keeps token leakage out of the URL.
  const downloadIdCard = async (): Promise<void> => {
    if (!fv) return;
    const res = await api.get<Blob>(`/frequent-visitors/${fv.id}/id-card.svg`, {
      responseType: 'blob'
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    const safe = fv.full_name.replace(/[^A-Za-z0-9-]+/g, '-').slice(0, 40) || 'staff';
    a.download = `hooddesk-id-${safe}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ───── loading / error states ─────

  if (fvQ.isLoading) {
    return (
      <>
        <PageHeader title="Frequent visitor" description="Loading staff profile…" />
        <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </>
    );
  }

  if (fvQ.isError || !fv) {
    return (
      <>
        <PageHeader title="Frequent visitor" description="Could not load this profile." />
        <div className="mx-auto w-full max-w-[1200px] px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Frequent visitor not found"
                description={
                  fvQ.error
                    ? getErrorMessage(fvQ.error)
                    : 'This record may have been removed or you may not have access.'
                }
                action={
                  <Button variant="secondary" onClick={() => void navigate({ to: '/frequent-visitors' })}>
                    <ArrowLeft className="h-4 w-4" /> Back to frequent visitors
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const kindLabel = kindRow?.label ?? fv.kind;
  const noIdCard = kindRow?.issue_id_card === false;
  const assignments = assignmentsQ.data?.items ?? [];
  const attendance = attendanceQ.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={fv.full_name}
        description={`${kindLabel} · ${fv.phone}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/frequent-visitors' })} title="Back to verified staff">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {fv.deleted_at ? (
          <div className="flex items-center gap-vt-2 rounded-vt-md border border-amber-300 bg-amber-50 px-vt-4 py-vt-2 text-vt-13 text-amber-900">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
            <span>Archived on {formatDateTime(fv.deleted_at)}</span>
          </div>
        ) : null}
        {/* ── Header card ─────────────────────────────────────────── */}
        <Card>
          <CardContent className="flex flex-col gap-vt-4 md:flex-row md:items-start">
            {/*
              Passport-style photo: 140×170 (3.5:4.5 — classic Indian passport
              ratio), rounded-vt-md, vt-border. Object-cover centers faces when
              the source aspect ratio differs. Initials fallback uses the same
              shape so layout doesn't jump as the blob loads.
            */}
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={fv.full_name}
                className="shrink-0 rounded-vt-md border border-vt-border object-cover"
                style={{ width: 140, height: 170 }}
              />
            ) : (
              <span
                className="flex shrink-0 items-center justify-center rounded-vt-md border border-vt-border bg-vt-primary-100 text-vt-32 font-semibold text-vt-primary"
                style={{ width: 140, height: 170 }}
                aria-hidden="true"
              >
                {initialsFor(fv.full_name)}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
                {fv.full_name}
              </h2>
              <div className="mt-vt-1 flex flex-wrap items-center gap-vt-3 text-vt-13 text-vt-text-muted">
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" strokeWidth={1.75} /> {fv.phone}
                </span>
                {fv.alt_phone ? (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" strokeWidth={1.75} /> Alt {fv.alt_phone}
                  </span>
                ) : null}
                {fv.emergency_contact_name || fv.emergency_contact_phone ? (
                  <span className="inline-flex items-center gap-1">
                    <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.75} />
                    SOS{' '}
                    {fv.emergency_contact_name ? `${fv.emergency_contact_name} · ` : ''}
                    {fv.emergency_contact_phone ?? '—'}
                  </span>
                ) : null}
              </div>
              <div className="mt-vt-2 flex flex-wrap items-center gap-vt-2">
                <Badge variant="muted" className="capitalize">
                  {kindLabel}
                </Badge>
                <Badge variant={STATUS_VARIANT[fv.status]}>{STATUS_LABEL[fv.status]}</Badge>
                {fv.active ? (
                  <Badge variant="ok">Active</Badge>
                ) : (
                  <Badge variant="muted">Inactive</Badge>
                )}
                <Badge variant={POLICE_VARIANT[fv.police_verification_status]}>
                  <ShieldCheck className="h-3 w-3" strokeWidth={1.75} />{' '}
                  {POLICE_LABEL[fv.police_verification_status]}
                </Badge>
                {fv.id_card_issued_at ? (
                  <Badge variant="ok">
                    <BadgeCheck className="h-3 w-3" strokeWidth={1.75} /> ID card issued
                  </Badge>
                ) : noIdCard ? (
                  <Badge variant="muted">No ID card (manual entry)</Badge>
                ) : (
                  <Badge variant="muted">ID card not issued</Badge>
                )}
                {fv.verified_at ? (
                  <Badge variant="muted">
                    <CalendarDays className="h-3 w-3" strokeWidth={1.75} />
                    Verified {formatDate(fv.verified_at)}
                  </Badge>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 1 · Personal & contact ─────────────────────── */}
        <Card>
          <CardContent>
            <SectionTitle
              icon={<UserCircle2 className="h-4 w-4" strokeWidth={1.75} />}
              title="Personal & contact"
              subtitle="Society-held identity + emergency contact info"
            />
            <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
              <LabelValue label="Full name">{fv.full_name}</LabelValue>
              <LabelValue label="Phone">{fv.phone}</LabelValue>
              <LabelValue label="Alternate phone">{fv.alt_phone ?? '—'}</LabelValue>
              <LabelValue label="Date of birth">
                {fv.dob ? formatDate(fv.dob) : '—'}
              </LabelValue>
              <LabelValue label="Kind">{kindLabel}</LabelValue>
              <LabelValue label="Created">{formatDateTime(fv.created_at)}</LabelValue>
              <LabelValue label="Current address (near society)">
                {fv.current_address ?? '—'}
              </LabelValue>
              <LabelValue label="Permanent / hometown address">
                {fv.permanent_address ?? '—'}
              </LabelValue>
              <LabelValue label="Emergency contact">
                {fv.emergency_contact_name || fv.emergency_contact_phone
                  ? `${fv.emergency_contact_name ?? '—'} · ${fv.emergency_contact_phone ?? '—'}`
                  : '—'}
              </LabelValue>
              <LabelValue label="Notes">{fv.notes ?? '—'}</LabelValue>
              {fv.rejection_reason ? (
                <LabelValue label="Rejection reason">{fv.rejection_reason}</LabelValue>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2 · KYC ─────────────────────────────────────── */}
        <Card>
          <CardContent>
            <SectionTitle
              icon={<ShieldCheck className="h-4 w-4" strokeWidth={1.75} />}
              title="KYC"
              subtitle="ID proof + police verification"
            />
            <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
              <LabelValue label="ID proof type">
                {fv.id_proof_type ? ID_PROOF_LABEL[fv.id_proof_type] : '—'}
              </LabelValue>
              <LabelValue label="ID number (last 4)">
                {fv.id_proof_last4 ? (
                  <span className="font-mono">···{fv.id_proof_last4}</span>
                ) : (
                  '—'
                )}
              </LabelValue>
              <LabelValue label="Police verification">
                <Badge variant={POLICE_VARIANT[fv.police_verification_status]}>
                  {POLICE_LABEL[fv.police_verification_status]}
                </Badge>
              </LabelValue>
              <LabelValue label="Police verified at">
                {fv.police_verified_at ? formatDateTime(fv.police_verified_at) : '—'}
              </LabelValue>
              <LabelValue label="ID proof scan">
                {idProofUrl ? (
                  <a
                    href={idProofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-vt-primary hover:underline"
                  >
                    <ImageIcon className="h-3.5 w-3.5" /> Open scan
                  </a>
                ) : fv.id_proof_storage_key ? (
                  <span className="text-vt-text-muted">Loading…</span>
                ) : (
                  <span className="text-vt-text-soft">No scan on file</span>
                )}
              </LabelValue>
              <LabelValue label="Police-verification doc">
                {fv.police_verification_storage_key ? (
                  <a
                    href={`/v1/frequent-visitors/${fv.id}/police-verification`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-vt-primary hover:underline"
                  >
                    <FileText className="h-3.5 w-3.5" /> Open doc
                  </a>
                ) : (
                  <span className="text-vt-text-soft">No doc on file</span>
                )}
              </LabelValue>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 3 · ID card ────────────────────────────────── */}
        <Card>
          <CardContent>
            <SectionTitle
              icon={<BadgeCheck className="h-4 w-4" strokeWidth={1.75} />}
              title="ID card"
              subtitle={
                noIdCard
                  ? `${kindLabel} does not get a printable QR card — gate entry is manual.`
                  : 'Permanent QR ID card issued to this staff member'
              }
              action={
                fv.id_card_issued_at ? (
                  <Button size="sm" variant="secondary" onClick={() => void downloadIdCard()}>
                    <Download className="h-4 w-4" /> Download SVG
                  </Button>
                ) : null
              }
            />
            <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
              <LabelValue label="Status">
                {fv.id_card_issued_at ? (
                  <Badge variant="ok">Issued</Badge>
                ) : noIdCard ? (
                  <Badge variant="muted">Manual entry only</Badge>
                ) : (
                  <Badge variant="muted">Not issued</Badge>
                )}
              </LabelValue>
              <LabelValue label="Issued at">
                {fv.id_card_issued_at ? formatDateTime(fv.id_card_issued_at) : '—'}
              </LabelValue>
              <LabelValue label="QR token">
                {fv.id_card_qr_token ? (
                  <span className="font-mono text-vt-12">{fv.id_card_qr_token}</span>
                ) : (
                  '—'
                )}
              </LabelValue>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 4 · Assigned flats ─────────────────────────── */}
        <Card>
          <CardContent>
            <SectionTitle
              icon={<Link2 className="h-4 w-4" strokeWidth={1.75} />}
              title="Assigned flats"
              subtitle={
                assignmentsQ.data
                  ? `${assignments.length} flat${assignments.length === 1 ? '' : 's'} linked`
                  : 'Loading…'
              }
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/frequent-visitors">Manage from list →</Link>
                </Button>
              }
            />
            {assignmentsQ.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : assignmentsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(assignmentsQ.error)}</p>
            ) : assignments.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No flats linked yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Flat</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Added by</TableHead>
                    <TableHead>Approval</TableHead>
                    <TableHead>Decided at</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1">
                          <Home className="h-3.5 w-3.5 text-vt-text-soft" />
                          {a.unit.block?.name ?? '—'} / {a.unit.number}
                        </span>
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        <div className="flex flex-wrap items-center gap-vt-2">
                          <CalendarDays className="h-3.5 w-3.5" />
                          {a.days_of_week === null
                            ? 'Any day'
                            : a.days_of_week === 'all'
                            ? 'All days'
                            : a.days_of_week
                                .split(',')
                                .map((d) => DAY_LABEL[d] ?? d)
                                .join(' · ')}
                          {a.time_start && a.time_end ? (
                            <>
                              <Clock3 className="ml-vt-1 h-3.5 w-3.5" />
                              {a.time_start}–{a.time_end}
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {a.added_by?.full_name ?? <span className="text-vt-text-soft">Admin</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          {a.active ? (
                            <Badge variant="ok">Active</Badge>
                          ) : (
                            <Badge variant="muted">Inactive</Badge>
                          )}
                          <Badge variant={APPROVAL_STATUS_VARIANT[a.approval_status]}>
                            {APPROVAL_STATUS_LABEL[a.approval_status]}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-vt-text-muted">
                        {a.approval_decided_at ? formatDateTime(a.approval_decided_at) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Section 5 · Attendance history ─────────────────────── */}
        <Card>
          <CardContent>
            <SectionTitle
              icon={<CalendarClock className="h-4 w-4" strokeWidth={1.75} />}
              title="Attendance — last 14 days"
              subtitle={
                attendanceQ.data
                  ? `${attendance.length} event${attendance.length === 1 ? '' : 's'} since ${formatDate(dateFrom)}`
                  : 'Gate IN / OUT events for this staff member'
              }
            />
            {attendanceQ.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : attendanceQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(attendanceQ.error)}</p>
            ) : attendance.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No attendance recorded in the last 14 days.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendance.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-vt-text-muted">{formatDateTime(r.ts)}</TableCell>
                      <TableCell>
                        <Badge variant={r.kind === 'in' ? 'ok' : 'muted'} className="uppercase">
                          {r.kind}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted capitalize">
                        {r.scan_method.replace('_', ' ')}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {r.unit ? `${r.unit.block?.name ?? ''} / ${r.unit.number}` : '—'}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {r.notes ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Section 6 · Audit trail ────────────────────────────── */}
        <Card>
          <CardContent>
            <SectionTitle
              icon={<History className="h-4 w-4" strokeWidth={1.75} />}
              title="Audit trail"
              subtitle="Recent admin actions on this profile (latest 10)"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link
                    to="/audit-log"
                    search={{ entity_type: 'frequent_visitor', entity_id: id } as never}
                  >
                    Open full log →
                  </Link>
                </Button>
              }
            />
            {auditQ.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : auditQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(auditQ.error)}</p>
            ) : (auditQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No audit events on file.</p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {(auditQ.data?.items ?? []).map((row) => (
                  <div key={row.id} className="flex items-start gap-vt-3 py-vt-2">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-vt-text-soft" strokeWidth={1.75} />
                    <div className="min-w-0 flex-1">
                      <div className="text-vt-13 font-medium text-vt-text">
                        {AUDIT_ACTION_LABEL[row.action] ?? row.action}
                      </div>
                      <div className="text-vt-12 text-vt-text-muted">
                        {row.actor_role} · {formatDateTime(row.at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
